// The one to-do form (#4319). Every place a to-do is created or edited renders
// TodoForm, and every single-to-do edit goes through TodoEditor + saveTodoEdit:
// the work row's Edit action (WorkActions), the to-do modal's Edit button
// (TodoDetailModal) and the pencil on both CS To-Do lists. So the three entry
// points submit the same payload by construction (todoForm.parity.test.js).
//
// Moved out of ClientOverview.js unchanged except where marked #4319/#4320, so
// the Work pages can open it without importing the whole Client Overview.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { I } from './coIcons';
import './ClientOverview.css';
import { nexifyTodoDescription, fetchTodo, updateTodo, appendAudit } from './api';
import { getStaffDirectory, getOneTodoMetadata, saveTodoMetadata } from '../../../services/workService';
import { getMeetings, meetingTime, dayKey } from '../../../services/meetingService';
import {
  useClientOwnerStore, ownerFor, setClientOwner, loadCsClients, useClientContacts,
} from '../../../services/clientOwners';

export { useClientOwnerStore, ownerFor, loadCsClients };

// Local copies of two ClientOverview.js helpers (they are used all over that
// file, so they stay there too).
const dateOnly = (v) => {
  if (!v) return null;
  const m = String(v).match(/^\d{4}-\d{2}-\d{2}/);
  return m ? m[0] : null;
};
const today = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ─────────────────────────────────────────────
// ModalOverlay — renders the dimmed overlay through a portal to document.body
// so the fixed overlay pins to the viewport, not to any transformed ancestor
// (the page content animates via co-fade-in, which would otherwise trap a
// position:fixed child and make modals appear far down the page). The
// display:contents .co-root wrapper carries the --co-* variables + scoped
// styles without generating a layout box.
// ─────────────────────────────────────────────
export function ModalOverlay({ onClose, children }) {
  return createPortal(
    <div className="co-root" style={{ display: 'contents' }}>
      <div className="co-modal-overlay" onClick={onClose}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export const TODO_PRIORITY = {
  Low:      { c: '#28c281', bg: 'rgba(40,194,129,.13)',  border: 'rgba(40,194,129,.3)'  },
  Medium:   { c: '#f0a93b', bg: 'rgba(240,169,59,.13)',  border: 'rgba(240,169,59,.3)'  },
  High:     { c: '#f0556d', bg: 'rgba(240,85,109,.13)',  border: 'rgba(240,85,109,.3)'  },
  Critical: { c: '#ffffff', bg: '#d4102e',               border: '#d4102e', filled: true },
};

// "Check in with" is a choice of three (Patrick, 2026-10-12, releases/STATUS.md
// → Decisions): the district person goes in Client owner instead. The stored
// values are unchanged ("Nexus Implementations Team" is what every existing
// to-do already holds), and a legacy free-text value is kept and shown as-is
// (checkinWithOptions) — nothing is migrated.
export const TODO_CHECKIN_WITH = ['Nexus Implementations Team', 'Client', 'Other'];

/** The select's options for a value: the three, plus a legacy value kept as it is. */
export const checkinWithOptions = (value) => (TODO_CHECKIN_WITH.includes(value)
  ? TODO_CHECKIN_WITH : [value, ...TODO_CHECKIN_WITH]);

export const EMPTY_TODO_FORM = {
  title: '', description: '', checkinDate: '', checkinWith: 'Nexus Implementations Team',
  assignedTo: '', priority: 'High', requestedBy: '', stakeholders: [], clientOwner: '',
};

// ─────────────────────────────────────────────
// One payload, one save (#4319)
// ─────────────────────────────────────────────

/** A to-do (as Chat returns it) + its ledger people → the form. */
export const todoToForm = (todo, { requestedBy = '', stakeholders = [], clientOwner = '' } = {}) => ({
  title: todo.title || '', description: todo.description || '', checkinDate: dateOnly(todo.checkinDate) || '',
  checkinWith: todo.checkinWith ?? 'Nexus Implementations Team', assignedTo: todo.assignedTo || '',
  priority: todo.priority || 'Medium', requestedBy, stakeholders, clientOwner,
  clientId: todo.clientId || '',
});

const sameId = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

/**
 * The body of Chat's PUT for an edit: exactly the fields Chat stores. clientId
 * only when the client really changed (a group "Edit all" never sends one, so
 * it cannot move every sibling onto one client).
 */
export const todoUpdatePayload = (form, originalClientId) => ({
  title: form.title,
  description: form.description,
  checkinDate: form.checkinDate,
  checkinWith: form.checkinWith,
  assignedTo: form.assignedTo,
  priority: form.priority,
  ...(form.clientId && !sameId(form.clientId, originalClientId) ? { clientId: form.clientId } : {}),
});

const PEOPLE_UNREAD = 'Requested by and Stakeholders were not saved: they could not be read, and saving would have erased them.';

/**
 * Save one to-do's edit, the same way from every entry point:
 *   1. Chat PUT (todoUpdatePayload) — the to-do itself, client move included;
 *   2. requested by + stakeholders to the item-metadata ledger (skipped when
 *      they could not be read, so they are never blanked);
 *   3. client owner (district) to the ledger, only when it changed;
 *   4. a client move is written to BOTH clients' audit trails.
 * Returns { todo, moved, warnings }: the to-do saved, the rest best-effort and
 * said out loud.
 */
export async function saveTodoEdit({ todo, form, metaUnavailable = false, ownerStatus = 'ready',
  originalOwner = '', clientTitleOf = () => '' }) {
  const payload = todoUpdatePayload(form, todo.clientId);
  const saved = await updateTodo(todo.clientId, todo.id, payload);
  const warnings = [];
  if (metaUnavailable) {
    warnings.push(PEOPLE_UNREAD);
  } else {
    await saveTodoMetadata(todo.id, { requestedBy: form.requestedBy || '', stakeholders: form.stakeholders || [] })
      .catch((e) => warnings.push(`Requested by and Stakeholders did not save (${e.message}).`));
  }
  const owner = (form.clientOwner || '').trim();
  if (ownerStatus === 'ready' && owner !== (originalOwner || '').trim()) {
    await setClientOwner({ source: 'todo', id: todo.id, title: todo.title, clientId: todo.clientId }, owner)
      .catch((e) => warnings.push(`Client owner did not save (${e.message}).`));
  }
  const moved = !!payload.clientId;
  if (moved) {
    const from = clientTitleOf(todo.clientId) || 'another client';
    const to = clientTitleOf(payload.clientId) || 'another client';
    const what = `to-do "${form.title.slice(0, 80)}"`;
    await Promise.all([
      appendAudit(todo.clientId, `Moved ${what} to ${to}`),
      appendAudit(payload.clientId, `Moved ${what} here from ${from}`),
    ]).catch(() => warnings.push('Moved, but the move was not written to the clients’ audit trails.'));
  }
  return { todo: saved, moved, warnings };
}

/** After a create: the client owner for each new to-do (the ledger, not Chat). */
export async function saveCreatedTodoExtras(created, clientOwner) {
  const owner = (clientOwner || '').trim();
  if (!owner) return [];
  const rows = created?.todos ?? (created ? [created?.todo ?? created] : []);
  const results = await Promise.all(rows.filter((t) => t?.id).map((t) => setClientOwner(
    { source: 'todo', id: t.id, title: t.title, clientId: t.clientId }, owner,
  ).then(() => null, (e) => e.message)));
  const failed = results.filter(Boolean);
  return failed.length ? [`Saved the to-do, but its Client owner did not save (${failed[0]}).`] : [];
}

// Shared <datalist> id for every owner/requestor/stakeholder input on the
// to-do form — one directory fetch (see useStaffDirectory below), reused by
// however many fields want to suggest from it via list={STAFF_DATALIST_ID}.
const STAFF_DATALIST_ID = 'nexus-staff-directory';

// Nexus staff, for pre-populating owner/requestor/stakeholder inputs with
// real people via a native <datalist> -- picking a suggestion stores their
// email, typing anything else (an external contact's email, or legacy free
// text) still works, since a datalist never constrains the input's value.
// Fetched once and cached in workService, so mounting this on several forms
// costs one network call, not one per form.
function useStaffDirectory() {
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getStaffDirectory().then((rows) => { if (!cancelled) setStaff(rows); });
    return () => { cancelled = true; };
  }, []);
  return staff;
}

function StaffDatalist({ staff }) {
  return (
    <datalist id={STAFF_DATALIST_ID}>
      {staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}
    </datalist>
  );
}

// A minimal multiselect: type a value, press Enter to add it as a chip.
// `listId`, when given, wires the draft input to a <datalist> so internal
// staff show as suggestions -- typing anything else (an external email)
// still works, a datalist never constrains the value.
function TagListField({ label, values, onChange, placeholder, listId }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v || values.includes(v)) { setDraft(''); return; }
    onChange([...values, v]);
    setDraft('');
  };
  return (
    <div className="co-form-field" style={{ gridColumn: '1 / -1' }}>
      <div className="co-form-field-label">{label}</div>
      <input
        className="co-input"
        placeholder={placeholder}
        value={draft}
        list={listId}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        onBlur={add}
      />
      {values.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
          {values.map((v) => (
            <span key={v} className="co-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {v}
              <button
                type="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'inherit' }}
                aria-label={`Remove ${v}`}
              >
                <I.x style={{ width: 11, height: 11 }} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Grows a textarea's height to fit its content as the user types
function autoGrow(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

// `single` (#4218/#4319): the edit form's picker — exactly one client, no
// "All Clients"; picking another one moves this to-do there.
export function TodoClientPicker({ clients, value, onChange, single = false, movedFrom = '' }) {
  const [query, setQuery] = useState('');
  const selected = new Set(value.clientIds);

  const toggleAll = () => {
    onChange(value.allClients ? { clientIds: [], allClients: false } : { clientIds: [], allClients: true });
  };
  const toggleClient = (clientId) => {
    if (single) { onChange({ clientIds: [clientId], allClients: false }); return; }
    if (value.allClients) return;
    const next = new Set(selected);
    next.has(clientId) ? next.delete(clientId) : next.add(clientId);
    onChange({ clientIds: [...next], allClients: false });
  };

  const filtered = query.trim()
    ? clients.filter((c) => (c.title || '').toLowerCase().includes(query.trim().toLowerCase()))
    : clients;

  return (
    <div className="co-form-field" style={{ gridColumn: '1 / -1' }}>
      <div className="co-form-field-label">{single ? 'Client' : 'Clients'}</div>
      <div style={{ border: '1px solid var(--co-border2)', borderRadius: 8, background: 'var(--co-bg2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid var(--co-border)' }}>
          {!single && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--co-txt)', cursor: 'pointer' }}>
              <input type="checkbox" checked={value.allClients} onChange={toggleAll} style={{ accentColor: 'var(--co-blue)' }} />
              All Clients
            </label>
          )}
          {!value.allClients && (
            <input
              className="co-input"
              placeholder="Search clients…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ marginLeft: 'auto', maxWidth: 200, padding: '4px 8px', fontSize: 12 }}
            />
          )}
        </div>
        {!value.allClients && (
          <div style={{ maxHeight: 180, overflowY: 'auto', padding: 4 }}>
            {filtered.map((c) => (
              <label key={c.clientId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', fontSize: 12.5, color: 'var(--co-txt2)', cursor: 'pointer', borderRadius: 5 }}>
                <input type={single ? 'radio' : 'checkbox'} name={single ? 'todo-client' : undefined}
                  checked={selected.has(c.clientId)} onChange={() => toggleClient(c.clientId)} style={{ accentColor: 'var(--co-blue)' }} />
                {c.title}
              </label>
            ))}
            {filtered.length === 0 && <div style={{ padding: '8px', fontSize: 12, color: 'var(--co-txt3)' }}>No matching clients</div>}
          </div>
        )}
        {value.allClients && (
          <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--co-txt3)' }}>Applies to every client.</div>
        )}
      </div>
      <div style={{ marginTop: 4, fontSize: 11.5, color: movedFrom ? 'var(--co-amber)' : 'var(--co-txt3)' }}>
        {single
          ? (movedFrom
            ? `Saving moves this to-do off ${movedFrom}'s lists. Only this to-do moves; if it was part of a multi-client to-do, it leaves that group.`
            : `${(clients.find((c) => selected.has(c.clientId)) || {}).title || 'This client'}`)
          : value.allClients ? `All ${clients.length} clients` : `${selected.size} client${selected.size === 1 ? '' : 's'} selected`}
      </div>
    </div>
  );
}

// Surfaces the client's soonest not-yet-happened captured meeting next to the
// Check-in Date field, so picking that date isn't a guess — a CSM can see
// what's already on the calendar with this district before committing to one.
// Only resolvable for a single, specific client (not "All clients" or a
// multi-client picker selection), since "next meeting" has no single answer
// across several districts.
function NextMeetingHint({ clientId, onUseDate }) {
  const [state, setState] = useState({ loading: false, meeting: null });

  useEffect(() => {
    if (!clientId) { setState({ loading: false, meeting: null }); return; }
    let cancelled = false;
    setState({ loading: true, meeting: null });
    getMeetings(clientId)
      .then((d) => {
        if (cancelled) return;
        const now = Date.now();
        const upcoming = (d.meetings || [])
          .filter((m) => !m.is_cancelled)
          .filter((m) => {
            const t = new Date(`${m.start_utc}Z`).getTime();
            return !Number.isNaN(t) && t >= now;
          })
          .sort((a, b) => new Date(`${a.start_utc}Z`) - new Date(`${b.start_utc}Z`));
        setState({ loading: false, meeting: upcoming[0] || null });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, meeting: null }); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (!clientId || state.loading) return null;
  if (!state.meeting) return null;
  const m = state.meeting;

  return (
    <div style={{
      marginTop: 6, fontSize: 11.5, color: 'var(--co-txt3)',
      display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
    }}>
      <I.cal style={{ width: 12, height: 12, flexShrink: 0 }} />
      <span>Next meeting: <strong style={{ color: 'var(--co-txt2)' }}>{meetingTime(m.start_utc)}</strong>
        {m.subject ? ` — ${m.subject}` : ''}
      </span>
      {onUseDate && (
        <button
          type="button"
          onClick={() => onUseDate(dayKey(m.start_utc))}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, fontWeight: 600, color: 'var(--co-blue)' }}
        >
          Use this date
        </button>
      )}
    </div>
  );
}

const CLIENT_CONTACTS_LIST_ID = 'nexus-client-contacts';

/**
 * Client owner (district), #4320: who at the district is working this. Picks
 * from the client's Executive sponsor / Champions / Key contacts, or anything
 * typed. Informational only: never nudged or emailed (the hint says so). When
 * the store could not be read the field is locked, so a save can't blank it.
 */
function ClientOwnerInput({ clientId, status, value, onChange }) {
  const contacts = useClientContacts(clientId);
  const unreadable = status === 'error';
  return (
    <div className="co-form-field" style={{ gridColumn: '1 / -1' }}>
      <div className="co-form-field-label">Client owner (district) (optional)</div>
      <input className="co-input" list={CLIENT_CONTACTS_LIST_ID} value={unreadable ? '' : value}
        aria-label="Client owner (district)"
        disabled={unreadable || status === 'loading' || status === 'idle'}
        placeholder={unreadable ? 'Couldn’t read the client owner — not changed by this save'
          : contacts.length ? 'Pick a district contact or type a name' : 'Name of the district person'}
        onChange={(e) => onChange(e.target.value)} />
      <datalist id={CLIENT_CONTACTS_LIST_ID}>
        {contacts.map((c) => <option key={c.name} value={c.name}>{c.hint}</option>)}
      </datalist>
      <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--co-txt3)' }}>
        For the team’s information only — Nexus never emails or nudges them.
      </div>
    </div>
  );
}

// `clientOwner` (#4320): { status } of the client-owner store, or omitted to
// hide the field (a multi-client "Edit all", where each client has its own).
// `saving` / `error`: TodoEditor's save state.
export function TodoForm({ form, setForm, onSave, onCancel, clientPicker, clientId, clientOwner, saving = false, error = null }) {
  const canSave = !saving && form.title.trim() && (!clientPicker || clientPicker.value.allClients || clientPicker.value.clientIds.length > 0);
  const staff = useStaffDirectory();
  // Only resolvable to a single client: either passed directly (the
  // per-client To-Do tab), or a picker with exactly one client checked.
  const meetingClientId = clientId
    || (clientPicker && !clientPicker.value.allClients && clientPicker.value.clientIds.length === 1
      ? clientPicker.value.clientIds[0] : null);
  const [nexifying, setNexifying] = useState(false);
  const [nexifyError, setNexifyError] = useState(null);
  const descriptionRef = useRef(null);

  const handleNexify = async () => {
    if (nexifying || !form.description.trim()) return;
    setNexifying(true);
    setNexifyError(null);
    try {
      const nexified = await nexifyTodoDescription(form.description, form.title);
      if (typeof nexified === 'string' && nexified.trim()) {
        setForm({ ...form, description: nexified });
        //this change was added to make the desc text input to autogrow for nexify inputs as well
        requestAnimationFrame(() => autoGrow(descriptionRef.current));
      }
    } catch (e) {
      setNexifyError(e.message);
    } finally {
      setNexifying(false);
    }
  };

  return (
    <div className="co-inline-form">
      <div className="co-inline-form-grid">
        {clientPicker && (
          <TodoClientPicker clients={clientPicker.clients} value={clientPicker.value} onChange={clientPicker.onChange}
            single={!!clientPicker.single} movedFrom={clientPicker.movedFrom || ''} />
        )}
        <div className="co-form-field" style={{ gridColumn: '1 / -1' }}>
          <div className="co-form-field-label">Title</div>
          <input className="co-input" placeholder="e.g. Confirm renewal date with district" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="co-form-field" style={{ gridColumn: '1 / -1' }}>
          <div className="co-form-field-label">Description</div>
          <textarea
            className="co-textarea"
            rows={1}
            style={{ resize: 'none', overflow: 'hidden' }}
            placeholder="Details / context"
            value={form.description}
            ref={(el) => { descriptionRef.current = el; autoGrow(el); }}
            onInput={(e) => autoGrow(e.target)}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 6 }}>
            {nexifyError && <span style={{ color: 'var(--co-red)', fontSize: 11.5 }}>{nexifyError}</span>}
            <button
              type="button"
              className="co-nexify-btn"
              onClick={handleNexify}
              disabled={nexifying || !form.description.trim()}
              title="Nexify the description"
            >
              <I.refresh className={nexifying ? 'co-spin' : ''} style={{ width: 13, height: 13 }} />
            </button>
          </div>
        </div>
        <div className="co-form-field">
          <div className="co-form-field-label">Check-in Date</div>
          <input className="co-input" type="date" min={today()} style={{ colorScheme: 'dark' }} value={form.checkinDate}
            onChange={(e) => setForm({ ...form, checkinDate: e.target.value })} />
          <NextMeetingHint clientId={meetingClientId} onUseDate={(d) => setForm({ ...form, checkinDate: d })} />
        </div>
        <div className="co-form-field">
          <div className="co-form-field-label">Check-in With</div>
          <select className="co-select" value={form.checkinWith}
            onChange={(e) => setForm({ ...form, checkinWith: e.target.value })}>
            {checkinWithOptions(form.checkinWith).map((o) => <option key={o} value={o}>{o || 'Not set'}</option>)}
          </select>
          {clientOwner && (
            <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--co-txt3)' }}>The person at the district goes in Client owner below.</div>
          )}
        </div>
        <div className="co-form-field">
          <div className="co-form-field-label">Owner (optional)</div>
          <input className="co-input" placeholder="Name or email" value={form.assignedTo}
            list={STAFF_DATALIST_ID}
            onChange={(e) => setForm({ ...form, assignedTo: e.target.value })} />
        </div>
        <div className="co-form-field">
          <div className="co-form-field-label">Priority</div>
          <select className="co-select" value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {Object.keys(TODO_PRIORITY).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className="co-form-field">
          <div className="co-form-field-label">Requested By (optional)</div>
          <input className="co-input" placeholder="Who asked for this" value={form.requestedBy || ''}
            list={STAFF_DATALIST_ID}
            onChange={(e) => setForm({ ...form, requestedBy: e.target.value })} />
        </div>
        <TagListField
          label="Stakeholders (optional)"
          values={form.stakeholders || []}
          onChange={(next) => setForm({ ...form, stakeholders: next })}
          placeholder="Email, then Enter"
          listId={STAFF_DATALIST_ID}
        />
        {clientOwner && (
          <ClientOwnerInput
            clientId={meetingClientId}
            status={clientOwner.status}
            value={form.clientOwner || ''}
            onChange={(v) => setForm({ ...form, clientOwner: v })}
          />
        )}
        <StaffDatalist staff={staff} />
      </div>
      {error && <div style={{ color: 'var(--co-red)', fontSize: 12.5, marginTop: 8 }}>{error}</div>}
      <div className="co-form-actions">
        <button className="co-btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="co-btn-primary" disabled={!canSave} onClick={onSave}
          style={{ opacity: canSave ? 1 : 0.4 }}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}

// Reuse the edit to-do form modal with optional client picker for add to-do
export function TodoEditModal({ title = 'Edit To-Do', clientName, clientId, form, setForm, onSave, onClose, clientPicker, clientOwner }) {
  return (
    <ModalOverlay onClose={onClose}>
      <div className="co-modal co-fade-in" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="co-modal-head">
          <div className="co-modal-title">{title}{clientName ? ` · ${clientName}` : ''}</div>
          <button className="co-btn-icon" onClick={onClose}><I.x style={{ width: 20, height: 20 }} /></button>
        </div>
        <div className="co-modal-body">
          <TodoForm form={form} setForm={setForm} onSave={onSave} onCancel={onClose} clientPicker={clientPicker} clientId={clientId}
            clientOwner={clientOwner} />
        </div>
      </div>
    </ModalOverlay>
  );
}

/**
 * Edit ONE to-do (#4319) — the only single-to-do editor. The row's Edit
 * action and the CS lists' pencil open it in TodoEditDialog; the to-do modal
 * renders it in place. It loads what it needs itself (the to-do unless given,
 * its ledger people, its client owner, the client list) so every entry point
 * starts from the same values and saves through saveTodoEdit.
 *
 * `onSaved({ todo, moved, warnings })` runs once the to-do itself saved;
 * warnings are the best-effort parts that did not.
 */
export function TodoEditor({ todo: given = null, todoId = '', onSaved, onCancel }) {
  const id = given?.id || todoId;
  const owners = useClientOwnerStore();
  const owner = ownerFor(owners, { source: 'todo', id });
  const ownerSettled = owner.status === 'ready' || owner.status === 'error';
  const [todo, setTodo] = useState(given);
  const [meta, setMeta] = useState(null);
  const [clients, setClients] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let alive = true;
    (given ? Promise.resolve(given) : fetchTodo(id))
      .then((t) => { if (alive) setTodo(t); }, (e) => { if (alive) setLoadError(`Couldn't load the to-do: ${e.message}`); });
    getOneTodoMetadata(id).then(
      (m) => { if (alive) setMeta({ requestedBy: m.requested_by || '', stakeholders: m.stakeholders || [], unavailable: false }); },
      () => { if (alive) setMeta({ requestedBy: '', stakeholders: [], unavailable: true }); },
    );
    loadCsClients().then((c) => { if (alive) setClients(c); });
    return () => { alive = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form || !todo || !meta || !ownerSettled) return;
    setForm(todoToForm(todo, { requestedBy: meta.requestedBy, stakeholders: meta.stakeholders, clientOwner: owner.value }));
  }, [form, todo, meta, ownerSettled, owner.value]);

  const clientTitleOf = useCallback((cid) => ((clients || []).find((c) => sameId(c.clientId, cid)) || {}).title || '', [clients]);

  if (loadError) return <div style={{ color: 'var(--co-red)', fontSize: 13 }}>{loadError}</div>;
  if (!form) return <div className="co-empty">Loading…</div>;

  // Active clients, plus the one it is on now even if that one is inactive.
  const pickerClients = (clients || [])
    .filter((c) => c.isActive !== false || sameId(c.clientId, todo.clientId))
    .slice().sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
  const moving = !!form.clientId && !sameId(form.clientId, todo.clientId);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await saveTodoEdit({
        todo, form, metaUnavailable: meta.unavailable, ownerStatus: owner.status,
        originalOwner: owner.value, clientTitleOf,
      });
      if (onSaved) onSaved(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {meta.unavailable && (
        <div style={{ color: 'var(--co-amber)', fontSize: 12, marginBottom: 8 }}>
          Couldn’t read Requested by and Stakeholders, so this save leaves them as they are.
        </div>
      )}
      {clients && !pickerClients.length && (
        <div style={{ color: 'var(--co-amber)', fontSize: 12, marginBottom: 8 }}>
          Couldn’t load the client list, so the client can’t be changed here.
        </div>
      )}
      <TodoForm
        form={form} setForm={setForm} onSave={save} onCancel={onCancel}
        clientPicker={pickerClients.length ? {
          single: true, clients: pickerClients,
          value: { clientIds: [form.clientId], allClients: false },
          onChange: (v) => setForm({ ...form, clientId: v.clientIds[0] || form.clientId }),
          movedFrom: moving ? (clientTitleOf(todo.clientId) || 'its current client') : '',
        } : null}
        clientId={form.clientId}
        clientOwner={{ status: owner.status }}
        saving={saving} error={error}
      />
    </>
  );
}

/** TodoEditor in a modal: the row's Edit action and the CS lists' pencil. */
export function TodoEditDialog({ todo = null, todoId = '', clientName = '', onSaved, onClose }) {
  return (
    <ModalOverlay onClose={onClose}>
      <div className="co-modal co-fade-in" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}
        role="dialog" aria-label="Edit To-Do">
        <div className="co-modal-head">
          <div className="co-modal-title">Edit To-Do{clientName ? ` · ${clientName}` : ''}</div>
          <button className="co-btn-icon" onClick={onClose} aria-label="Close"><I.x style={{ width: 20, height: 20 }} /></button>
        </div>
        <div className="co-modal-body">
          <TodoEditor todo={todo} todoId={todoId} onSaved={onSaved} onCancel={onClose} />
        </div>
      </div>
    </ModalOverlay>
  );
}
